import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";

type Platform = "twitter" | "bluesky" | "linkedin" | "threads";

const PLATFORM_LABEL: Record<string, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  threads: "Threads",
};

interface SocialAccountLite {
  id: string;
  platform: Platform;
  handle: string;
  display_name: string | null;
  status: string;
}

interface VoiceProfileLite {
  id: string;
  name: string;
  status: string;
}

interface EvergreenPost {
  id: string;
  name: string;
  social_account_id: string;
  platform: string;
  account_handle: string | null;
  account_display_name: string | null;
  voice_profile_id: string | null;
  voice_profile_name: string | null;
  ai_model: string | null;
  original_content: string;
  cadence_days: number;
  max_repeats: number | null;
  repeat_count: number;
  status: "active" | "paused" | "completed";
  last_reposted_at: number | null;
  next_due_at: number;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

interface EvergreenRun {
  id: string;
  rewritten_content: string | null;
  scheduled_post_id: string | null;
  status: string;
  error_message: string | null;
  created_at: number;
  scheduled_post_status: string | null;
  published_at: number | null;
  external_post_url: string | null;
}

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  paused: "bg-amber-100 text-amber-800",
  completed: "bg-ink-100 text-ink-700",
};

function fmtDate(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function fmtRelative(ms: number): string {
  const diff = ms - Date.now();
  const days = Math.round(diff / (24 * 60 * 60 * 1000));
  if (Math.abs(days) >= 1) return days >= 0 ? `in ${days}d` : `${-days}d ago`;
  const hours = Math.round(diff / (60 * 60 * 1000));
  if (Math.abs(hours) >= 1) return hours >= 0 ? `in ${hours}h` : `${-hours}h ago`;
  return diff >= 0 ? "soon" : "due now";
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default function EvergreenPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [posts, setPosts] = useState<EvergreenPost[]>([]);
  const [accounts, setAccounts] = useState<SocialAccountLite[]>([]);
  const [voices, setVoices] = useState<VoiceProfileLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<EvergreenPost | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showRunsFor, setShowRunsFor] = useState<EvergreenPost | null>(null);

  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [content, setContent] = useState("");
  const [cadence, setCadence] = useState(14);
  const [maxRepeats, setMaxRepeats] = useState<number | "">("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewText, setPreviewText] = useState<string | null>(null);

  const active = useMemo(() => posts.filter((p) => p.status === "active"), [posts]);
  const paused = useMemo(() => posts.filter((p) => p.status === "paused"), [posts]);
  const completed = useMemo(() => posts.filter((p) => p.status === "completed"), [posts]);

  async function load() {
    setLoading(true);
    try {
      const [ev, sa, vp] = await Promise.all([
        api.get<{ evergreen_posts: EvergreenPost[] }>("/api/evergreen"),
        api.get<{ social_accounts: SocialAccountLite[] }>("/api/social-accounts"),
        api.get<{ voice_profiles: VoiceProfileLite[] }>("/api/voice-profiles"),
      ]);
      setPosts(ev.evergreen_posts ?? []);
      setAccounts((sa.social_accounts ?? []).filter((a) => a.status === "active"));
      setVoices((vp.voice_profiles ?? []).filter((v) => v.status === "ready"));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetForm() {
    setName("");
    setAccountId(accounts[0]?.id ?? "");
    setVoiceId("");
    setContent("");
    setCadence(14);
    setMaxRepeats("");
    setPreviewText(null);
    setEditing(null);
    setShowForm(false);
  }

  function openCreate() {
    resetForm();
    setAccountId(accounts[0]?.id ?? "");
    setShowForm(true);
  }

  function openEdit(p: EvergreenPost) {
    setName(p.name);
    setAccountId(p.social_account_id);
    setVoiceId(p.voice_profile_id ?? "");
    setContent(p.original_content);
    setCadence(p.cadence_days);
    setMaxRepeats(p.max_repeats ?? "");
    setPreviewText(null);
    setEditing(p);
    setShowForm(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!accountId) {
      toast.error("Pick a social account.");
      return;
    }
    if (content.trim().length < 10) {
      toast.error("Content too short.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim() || content.slice(0, 60),
        social_account_id: accountId,
        voice_profile_id: voiceId || null,
        original_content: content,
        cadence_days: cadence,
        max_repeats: typeof maxRepeats === "number" ? maxRepeats : null,
      };
      if (editing) {
        await api.patch(`/api/evergreen/${editing.id}`, payload);
        toast.success("Updated.");
      } else {
        await api.post("/api/evergreen", payload);
        toast.success("Evergreen post added. First repost in " + cadence + " days.");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function togglePause(p: EvergreenPost) {
    try {
      await api.patch(`/api/evergreen/${p.id}`, {
        status: p.status === "active" ? "paused" : "active",
      });
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function remove(p: EvergreenPost) {
    const ok = await confirm({
      title: "Delete evergreen post",
      message: `"${p.name}" and its repost history will be permanently deleted.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/evergreen/${p.id}`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function previewRewrite() {
    if (!accountId || content.trim().length < 10) {
      toast.error("Pick an account and enter content first.");
      return;
    }
    const acct = accounts.find((a) => a.id === accountId);
    if (!acct) return;
    setPreviewLoading(true);
    try {
      const res = await api.post<{ content: string }>(
        "/api/evergreen/preview-rewrite",
        {
          voice_profile_id: voiceId || null,
          original_content: content,
          platform: acct.platform,
          iteration: 0,
        }
      );
      setPreviewText(res.content);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-sm text-ink-500">
        Loading…
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="page-title">Evergreen queue</h1>
          <p className="page-subtitle">
            Post a high-value piece once, recycle it forever. Each repost is
            AI-rewritten in your voice so the timeline never sees the same words
            twice.
          </p>
        </div>
        <button onClick={openCreate} className="btn-brand">
          + New evergreen post
        </button>
      </header>

      {accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          You need at least one connected social account.{" "}
          <Link to="/app/settings" className="font-medium underline">
            Connect one in settings
          </Link>
          .
        </div>
      ) : null}

      {showForm ? (
        <form
          onSubmit={handleSubmit}
          className="mb-6 rounded-xl border border-ink-200 bg-white p-5 shadow-sm"
        >
          <h2 className="mb-3 text-base font-medium text-ink-900">
            {editing ? "Edit evergreen post" : "New evergreen post"}
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input text-sm"
                placeholder="e.g. Leading indicators framework"
                maxLength={120}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Account
              </label>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="input text-sm"
                required
              >
                <option value="">Pick an account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {PLATFORM_LABEL[a.platform] ?? a.platform} · @{a.handle}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Voice profile
              </label>
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="input text-sm"
              >
                <option value="">No voice (use original tone)</option>
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Original content
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
                className="input text-sm font-mono"
                placeholder="Paste a high-value post or write one. Each recycle, the AI rewrites it from a new angle but keeps the substance."
                maxLength={8000}
                required
              />
              <p className="mt-1 text-xs text-ink-500">
                {content.length} chars
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Cadence (days)
              </label>
              <input
                type="number"
                value={cadence}
                onChange={(e) => setCadence(parseInt(e.target.value, 10) || 14)}
                min={3}
                max={365}
                className="input text-sm"
              />
              <p className="mt-1 text-xs text-ink-500">
                Repost every {cadence} day{cadence === 1 ? "" : "s"}
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">
                Max repeats (optional)
              </label>
              <input
                type="number"
                value={maxRepeats}
                onChange={(e) => {
                  const v = e.target.value;
                  setMaxRepeats(v === "" ? "" : parseInt(v, 10) || "");
                }}
                min={1}
                max={50}
                className="input text-sm"
                placeholder="Unlimited"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={previewRewrite}
                disabled={previewLoading}
                className="btn-secondary text-xs"
              >
                {previewLoading ? "Rewriting…" : "↻ Preview AI rewrite"}
              </button>
              {previewText ? (
                <div className="mt-2 rounded-lg border border-brand-200 bg-brand-50/30 p-3 text-sm">
                  <p className="mb-1 text-xs font-medium text-brand-800">
                    Preview (iteration 1) — actual reposts use a different angle each time
                  </p>
                  <p className="whitespace-pre-wrap text-ink-800">{previewText}</p>
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2 border-t border-ink-100 pt-3">
            <button type="button" onClick={resetForm} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save changes" : "Add to queue"}
            </button>
          </div>
        </form>
      ) : null}

      <Section
        title="Active"
        count={active.length}
        empty="No active evergreen posts. Add one to start recycling."
      >
        {active.map((p) => (
          <EvergreenCard
            key={p.id}
            p={p}
            onEdit={() => openEdit(p)}
            onTogglePause={() => togglePause(p)}
            onDelete={() => remove(p)}
            onShowRuns={() => setShowRunsFor(p)}
          />
        ))}
      </Section>

      {paused.length > 0 ? (
        <Section title="Paused" count={paused.length}>
          {paused.map((p) => (
            <EvergreenCard
              key={p.id}
              p={p}
              onEdit={() => openEdit(p)}
              onTogglePause={() => togglePause(p)}
              onDelete={() => remove(p)}
              onShowRuns={() => setShowRunsFor(p)}
            />
          ))}
        </Section>
      ) : null}

      {completed.length > 0 ? (
        <Section title="Completed" count={completed.length}>
          {completed.map((p) => (
            <EvergreenCard
              key={p.id}
              p={p}
              onEdit={() => openEdit(p)}
              onTogglePause={() => togglePause(p)}
              onDelete={() => remove(p)}
              onShowRuns={() => setShowRunsFor(p)}
            />
          ))}
        </Section>
      ) : null}

      {showRunsFor ? (
        <RunsModal post={showRunsFor} onClose={() => setShowRunsFor(null)} />
      ) : null}
    </div>
  );
}

function Section({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  count: number;
  empty?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-medium text-ink-900">
        {title} <span className="text-ink-500">({count})</span>
      </h2>
      {count === 0 && empty ? (
        <div className="rounded-lg border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
          {empty}
        </div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </section>
  );
}

function EvergreenCard(props: {
  p: EvergreenPost;
  onEdit: () => void;
  onTogglePause: () => void;
  onDelete: () => void;
  onShowRuns: () => void;
}) {
  const { p } = props;
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-[11px] ${STATUS_BADGE[p.status] ?? "bg-ink-100 text-ink-700"}`}
        >
          {p.status}
        </span>
        <span className="text-xs text-ink-500">
          {PLATFORM_LABEL[p.platform] ?? p.platform} · @{p.account_handle}
        </span>
        <span className="text-xs text-ink-400">·</span>
        <span className="text-xs text-ink-500">
          every {p.cadence_days}d
        </span>
        {p.voice_profile_name ? (
          <>
            <span className="text-xs text-ink-400">·</span>
            <span className="text-xs text-ink-500">
              voice: {p.voice_profile_name}
            </span>
          </>
        ) : null}
      </div>
      <h3 className="mb-1 text-sm font-medium text-ink-900">{p.name}</h3>
      <p className="mb-3 line-clamp-3 whitespace-pre-wrap text-sm text-ink-700">
        {truncate(p.original_content, 280)}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 pt-2 text-xs text-ink-500">
        <div className="flex flex-wrap gap-3">
          <span>
            Reposts: {p.repeat_count}
            {p.max_repeats ? `/${p.max_repeats}` : ""}
          </span>
          {p.last_reposted_at ? (
            <span>Last: {fmtDate(p.last_reposted_at)}</span>
          ) : null}
          <span>
            Next: {p.status === "active" ? fmtRelative(p.next_due_at) : "—"}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={props.onShowRuns}
            className="text-brand-700 hover:underline"
          >
            View runs
          </button>
          <button onClick={props.onEdit} className="text-brand-700 hover:underline">
            Edit
          </button>
          <button
            onClick={props.onTogglePause}
            className="text-brand-700 hover:underline"
          >
            {p.status === "active" ? "Pause" : "Resume"}
          </button>
          <button onClick={props.onDelete} className="text-red-600 hover:underline">
            Delete
          </button>
        </div>
      </div>
      {p.last_error ? (
        <p className="mt-2 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700">
          Last error: {p.last_error}
        </p>
      ) : null}
    </div>
  );
}

function RunsModal({
  post,
  onClose,
}: {
  post: EvergreenPost;
  onClose: () => void;
}) {
  const toast = useToast();
  const [runs, setRuns] = useState<EvergreenRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ runs: EvergreenRun[] }>(`/api/evergreen/${post.id}/runs`)
      .then((res) => setRuns(res.runs ?? []))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-2xl rounded-2xl border border-ink-200/60 bg-white p-5 shadow-xl animate-scale-in">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-ink-900">{post.name}</h3>
            <p className="text-xs text-ink-500">Repost history (latest first)</p>
          </div>
          <button onClick={onClose} className="text-ink-500 hover:text-ink-900">
            ×
          </button>
        </div>
        <div className="mb-3 rounded-lg bg-ink-50 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-500">
            Original
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-ink-800">
            {post.original_content}
          </p>
        </div>
        {loading ? (
          <p className="text-sm text-ink-500">Loading…</p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-ink-500">
            No repost runs yet. The first one will fire at {fmtDate(post.next_due_at)}.
          </p>
        ) : (
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {runs.map((r, i) => (
              <div
                key={r.id}
                className="rounded-lg border border-ink-200 p-3 text-sm"
              >
                <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-ink-500">
                  <span className="font-medium text-ink-700">
                    Repost #{runs.length - i}
                  </span>
                  <span>·</span>
                  <span>{fmtDate(r.created_at)}</span>
                  <span>·</span>
                  <span
                    className={`rounded-full px-2 py-0.5 ${r.status === "scheduled" ? "bg-blue-100 text-blue-800" : r.status === "failed" ? "bg-red-100 text-red-800" : "bg-ink-100 text-ink-700"}`}
                  >
                    {r.status}
                  </span>
                  {r.scheduled_post_status ? (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-800">
                      published: {r.scheduled_post_status}
                    </span>
                  ) : null}
                </div>
                {r.rewritten_content ? (
                  <p className="whitespace-pre-wrap text-ink-800">
                    {r.rewritten_content}
                  </p>
                ) : null}
                {r.error_message ? (
                  <p className="mt-1 text-xs text-red-700">{r.error_message}</p>
                ) : null}
                {r.external_post_url ? (
                  <a
                    href={r.external_post_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block text-xs text-brand-700 hover:underline"
                  >
                    View post →
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
